'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Clock, ArrowRight } from 'lucide-react';
import { Post } from '@/lib/types';
import { getCategoryInfo } from '@/lib/categories';
import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
interface ArticleCardProps {
  post: Post;
  featured?: boolean;
}
export function ArticleCard({
  post,
  featured = false
}: ArticleCardProps) {
  const categoryInfo = getCategoryInfo(post.category);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY
  }: React.MouseEvent) {
    const {
      left,
      top
    } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }
  if (featured) {
    return <Link href={`/${post.category}/${post.slug}`} className="group block h-full">
        <motion.article initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} whileHover={{
        scale: 1.02
      }} transition={{
        duration: 0.3
      }} className="relative h-full rounded-3xl overflow-hidden bg-background/30 backdrop-blur-md border border-white/10 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

          <div className="relative h-80 overflow-hidden">
            {post.image ? <Image src={post.image} alt={post.title} fill sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" className="object-cover transition-transform duration-700 group-hover:scale-110" quality={100} /> : <div className={`w-full h-full bg-gradient-to-br ${categoryInfo?.gradient || 'from-primary to-primary-600'}`} />}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />

            {/* Category Badge */}
            <div className="absolute top-6 left-6 z-10">
              <span className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-black/50 backdrop-blur-md text-white border border-white/20 shadow-lg">
                {categoryInfo?.name || post.category}
              </span>
            </div>
          </div>

          <div className="relative p-8 z-10">
            <div className="bg-background/40 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl transition-all duration-300 group-hover:border-primary/30 group-hover:bg-background/60">
              <h2 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70 group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h2>

              <p className="text-muted-foreground mb-6 line-clamp-2 text-lg">
                {post.description}
              </p>

              <div className="flex items-center justify-between text-sm text-muted-foreground/80 font-medium">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-primary mr-2" aria-hidden="true" />
                    <span className="sr-only">Category:</span>
                    {categoryInfo?.name || post.category}
                  </span>
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-primary" aria-hidden="true" />
                    <span className="sr-only">Read time:</span>
                    {post.readTime} min read
                  </span>
                </div>

                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <ArrowRight className="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform duration-300" aria-hidden="true" />
                  <span className="sr-only">Read article</span>
                </div>
              </div>
            </div>
          </div>
        </motion.article>
      </Link>;
  }
  return <Link href={`/${post.category}/${post.slug}`} className="group block h-full">
      <motion.div onMouseMove={handleMouseMove} className="group/card relative h-full flex flex-col rounded-3xl overflow-hidden bg-background/20 backdrop-blur-sm border border-white/5 hover:border-white/20 transition-colors duration-500" whileHover={{
      y: -10
    }}>
        <motion.div className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover/card:opacity-100" style={{
        background: useMotionTemplate`
                radial-gradient(
                    650px circle at ${mouseX}px ${mouseY}px,
                    rgba(var(--primary), 0.15),
                    transparent 80%
                )
                `
      }} />

        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          {post.image ? <Image src={post.image} alt={post.title} fill sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" className="object-cover transition-transform duration-700 group-hover:scale-110" quality={100} /> : <div className={`w-full h-full bg-gradient-to-br ${categoryInfo?.gradient || 'from-primary to-primary-600'}`} />}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />

          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-md bg-black/40 text-white border border-white/10 ${categoryInfo?.color || 'text-primary'}`}>
              {categoryInfo?.name || post.category}
            </span>
          </div>
        </div>

        <div className="relative p-6 flex flex-col flex-1">
          <h2 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
            {post.title}
          </h2>

          <p className="text-sm text-muted-foreground mb-6 line-clamp-3 leading-relaxed">
            {post.description}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-white/5 mt-auto">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground font-medium">
              <span className="flex items-center">
                <Clock className="w-3 h-3 mr-1.5 text-primary/70" aria-hidden="true" />
                <span className="sr-only">Read time:</span>
                {post.readTime}m
              </span>
              <span className="flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-primary/70 mr-1.5" aria-hidden="true" />
                <span className="sr-only">Category:</span>
                {categoryInfo?.name || post.category}
              </span>
            </div>

            <span className="text-xs font-semibold text-primary flex items-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
              Read Article <span className="sr-only">about {post.title}</span> <ArrowRight className="w-3 h-3 ml-1" aria-hidden="true" />
            </span>
          </div>
        </div>
      </motion.div>
    </Link>;
}